import { useCallback, useState } from 'react';
import { requestChatMappingRule } from 'utils/pendingChatMappingRule';
import type { MappingRuleDetails, MappingRuleMode, MappingRuleStepKind } from './types';

// Opens the Settings drawer straight onto Configurations. The same two query
// params the chat's own navigation targets use (see SETTINGS_PANELS in
// aiAssistantService.ts) and that layouts/Global reads to open the drawer.
const CONFIGURATIONS_PATH = '/?openDrawer=true&drawerSelection=Configurations';

interface Params {
	appendMessage: (msg: { text: string; mappingRuleStep?: MappingRuleStepKind }) => void;
	setActiveStep: (step: MappingRuleStepKind) => void;
	// Navigates to Configurations and closes the chat panel. Supplied by
	// ChatBubble, which owns both.
	goToConfigurations: (path: string) => void;
}

/**
 * Drives the Custom GL Mapping Rule handoff as chat cards. Deliberately much
 * smaller than SmartImportChat/DirectAllocationChat: those flows own their
 * whole wizard, whereas this one only collects a name and description and then
 * defers to the Add Rule modal, so there is no option list to fetch, nothing
 * to validate here, and no write of its own.
 */
export function useMappingRuleChat({ appendMessage, setActiveStep, goToConfigurations }: Params) {
	const [step, setStep] = useState<MappingRuleStepKind | null>(null);
	const [mode, setMode] = useState<MappingRuleMode>('create');
	const [details, setDetails] = useState<MappingRuleDetails>({ name: '', description: '' });
	const [error, setError] = useState<string | null>(null);

	const open = useCallback(
		(openMode: MappingRuleMode = 'create', ruleId?: string) => {
			setMode(openMode);
			setError(null);
			setDetails({ name: '', description: '' });

			// Editing needs nothing typed — the chat already resolved which rule,
			// so it goes straight to the modal with that rule loaded.
			if (openMode === 'edit' && ruleId) {
				requestChatMappingRule({ ruleId });
				setStep('handed_off');
				appendMessage({ text: '', mappingRuleStep: 'handed_off' });
				goToConfigurations(CONFIGURATIONS_PATH);
				return;
			}

			setStep('details');
			appendMessage({ text: '', mappingRuleStep: 'details' });
		},
		[appendMessage, goToConfigurations]
	);

	const patchDetails = useCallback((patch: Partial<MappingRuleDetails>) => {
		setDetails((prev) => ({ ...prev, ...patch }));
		setError(null);
	}, []);

	// The name is the one thing the modal requires and the one thing a picker
	// cannot supply, so it is checked here rather than leaving the user to
	// discover it after the drawer opens.
	const handOff = useCallback(() => {
		const name = details.name.trim();
		if (!name) {
			setError('Give the rule a name first.');
			return;
		}
		requestChatMappingRule({ name, description: details.description.trim() });
		setStep('handed_off');
		setActiveStep('handed_off');
		goToConfigurations(CONFIGURATIONS_PATH);
	}, [details, setActiveStep, goToConfigurations]);

	return { step, mode, details, error, open, patchDetails, handOff };
}

export type MappingRuleChat = ReturnType<typeof useMappingRuleChat>;
